import DocumentViewerModal from "components/DocumentViewer";
import FileActions from "components/FileDownloadButtons";
import { useHasRole } from "contexts/AuthContext";
import {
  Award,
  Calendar,
  CheckCircle,
  ChevronRight,
  Clock,
  Download,
  FileText,
  Gavel,
  MessageSquare,
  Scale,
} from "lucide-react";
import { useState } from "react";
import { CouncilLitigation, Litigation } from "types";
import { ADVOCATE_ROLES } from "utils/roles";
import { useTranslation } from "contexts/i18nContext";

interface LitigationProps {
  fromCourt?: Boolean;
  litigationData: Litigation[];
  councilLitigationData: CouncilLitigation[] | [];
  onLitigationRefresh: any;
}

export default function AppealLitigationCardForCourt({
  fromCourt = false,
  litigationData,
  councilLitigationData,
  onLitigationRefresh,
}: LitigationProps) {
  const { t } = useTranslation();
  const canViewOnlyDecision = useHasRole([
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_OFFICE_OF_COUNCIL_COORDINATOR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);

  const [expandedHearing, setExpandedHearing] = useState<string | null>(null);

  const hasCouncilDecision = councilLitigationData[0]?.council_decision_day;

  // Sort and filter data
  const sortedData = [...(litigationData || [])]
    .sort((a, b) => {
      const aHasDecision = !!a.council_decision_summary;
      const bHasDecision = !!b.council_decision_summary;

      // If only one has a decision, show it first
      if (aHasDecision && !bHasDecision) return -1;
      if (!aHasDecision && bHasDecision) return 1;

      // Otherwise, sort by next appointment date ascending
      const dateA = new Date(a?.next_appointment_date ?? "").getTime();
      const dateB = new Date(b?.next_appointment_date ?? "").getTime();

      return dateA - dateB;
    })
    .filter((obj) => !obj.council_decision_day);

  const toggleHearingExpansion = (id: number | string | undefined) => {
    if (id === undefined || id === null) return;
    const idStr = String(id);
    setExpandedHearing((prev) => (prev === idStr ? null : idStr));
  };

  return (
    <div className="space-y-6">
      {/* Daily Hearings */}
      {sortedData.length > 0 && (
        <div className="space-y-6">
          <div className="space-y-6">
            {sortedData.map((obj) =>
              canViewOnlyDecision ? (
                <div
                  key={obj.id}
                  className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border hover:shadow-md transition-all duration-200 ${
                    obj?.council_decision_summary
                      ? "border-l-4 border-dark-red-500 dark:border-dark-red-400"
                      : "border-l-4 border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {/* Hearing Header - Collapsible */}
                  <div
                    className="p-5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-t-xl transition-colors"
                    onClick={() => toggleHearingExpansion(obj.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div
                          className={`p-3 rounded-lg ${
                            obj?.council_decision_summary
                              ? "bg-dark-red-50 dark:bg-dark-red-900/20"
                              : "bg-gray-100 dark:bg-gray-700"
                          }`}
                        >
                          <Gavel
                            className={`w-5 h-5 ${
                              obj?.council_decision_summary
                                ? "text-dark-red-600 dark:text-dark-red-400"
                                : "text-gray-600 dark:text-gray-400"
                            }`}
                          />
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                              {t("appealHearingSession")}
                            </h3>
                            {obj?.council_decision_summary && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-dark-red-50 dark:bg-dark-red-900/30 text-dark-red-700 dark:text-dark-red-300 text-xs font-medium">
                                <CheckCircle className="w-3 h-3" />
                                {t("decisionMade")}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {obj?.next_appointment_date && (
                          <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-red-50 to-indigo-50 dark:from-red-900/20 dark:to-indigo-900/20 rounded-full border border-red-200 dark:border-red-800">
                            <Clock className="w-4 h-4 text-red-600 dark:text-red-400" />
                            <div className="text-xs">
                              <div className="font-semibold text-red-700 dark:text-red-300">
                                {t("nextHearing")}
                              </div>
                              <div className="text-red-600 dark:text-red-400">
                                {obj.next_appointment_date}
                              </div>
                            </div>
                          </div>
                        )}
                        <ChevronRight
                          className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform ${
                            expandedHearing === String(obj.id)
                              ? "rotate-90"
                              : ""
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Collapsible Content */}
                  {expandedHearing === String(obj.id) && (
                    <div className="px-5 pb-5 border-t border-gray-100 dark:border-gray-700 pt-5 space-y-5">
                      {/* Council Order (if not shown in header) */}
                      {obj?.given_order && (
                        <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                          <div className="flex items-center gap-3 mb-2">
                            <Scale className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            <h4 className="font-medium text-gray-700 dark:text-gray-300">
                              {t("councilOrder")}
                            </h4>
                          </div>
                          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-1">
                            {obj.given_order}
                          </p>
                        </div>
                      )}

                      {/* Decision Information (when decision is provided) */}
                      {obj?.council_decision_summary && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {obj?.council_decision_day && (
                            <div className="bg-white dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                              <div className="flex items-center gap-2 mb-2">
                                <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  {t("decisionDate")}
                                </h4>
                              </div>
                              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                                {obj.council_decision_day}
                              </p>
                            </div>
                          )}

                          {obj?.ammended_council_decision_type && (
                            <div className="bg-white dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                              <div className="flex items-center gap-2 mb-2">
                                <Scale className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  {t("decisionType")}
                                </h4>
                              </div>
                              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                                {obj.ammended_council_decision_type}
                              </p>
                            </div>
                          )}

                          {obj?.ammended_discipline_measure_type && (
                            <div className="bg-white dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                              <div className="flex items-center gap-2 mb-2">
                                <Gavel className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  {t("measureType")}
                                </h4>
                              </div>
                              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                                {obj.ammended_discipline_measure_type}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Summary Sections */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {obj?.daily_report_summary && (
                          <div className="bg-white dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t("dailyReportSummary")}
                              </h4>
                            </div>
                            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                              {obj.daily_report_summary}
                            </p>
                          </div>
                        )}

                        {obj?.appointment_reason && (
                          <div className="bg-white dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="flex items-center gap-2 mb-2">
                              <MessageSquare className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t("appointmentReason")}
                              </h4>
                            </div>
                            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                              {obj.appointment_reason}
                            </p>
                          </div>
                        )}

                        {obj?.council_decision_summary && (
                          <div className="md:col-span-2 bg-dark-red-50 dark:bg-dark-red-900/20 p-4 rounded-lg border border-dark-red-100 dark:border-dark-red-800">
                            <div className="flex items-center gap-2 mb-2">
                              <Award className="w-4 h-4 text-dark-red-600 dark:text-dark-red-400" />
                              <h4 className="text-sm font-medium text-dark-red-700 dark:text-dark-red-300">
                                {t("councilDecisionSummary")}
                              </h4>
                            </div>
                            <p className="text-dark-red-800 dark:text-dark-red-200 text-sm leading-relaxed">
                              {obj.council_decision_summary}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Files Section */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                            <Download className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          </div>
                          <h4 className="font-medium text-gray-700 dark:text-gray-300">
                            {t("hearingDocuments")}
                          </h4>
                        </div>

                        <DocumentViewerModal
                          documents={obj?.verdict_documents || []}
                          baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                          layout="list"
                          hideEmptyFile={true}
                        />

                        {/* Special Files */}
                        <div className="space-y-3">
                          {obj?.council_decision && (
                            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-gray-200 dark:bg-gray-600 rounded">
                                  <FileText className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {t("councilDecisionFile")}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {t("officialDecisionDocument")}
                                  </p>
                                </div>
                              </div>
                              <FileActions fileUrl={obj.council_decision} />
                            </div>
                          )}

                          {obj?.termination_document && (
                            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-gray-200 dark:bg-gray-600 rounded">
                                  <FileText className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {t("terminationFile")}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {t("caseTerminationDocument")}
                                  </p>
                                </div>
                              </div>
                              <FileActions fileUrl={obj.termination_document} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null,
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!hasCouncilDecision && sortedData.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <FileText className="w-10 h-10 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t("noAppealLitigationData")}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
            {t("noAppealLitigationMessage")}
          </p>
        </div>
      )}
    </div>
  );
}
